import { isHeicFile } from "@base/lib/isHeicFile";
import { cn } from "@base/lib/utils";
import type { ImageUploadItem } from "@base/model/hooks/useImageUploadQueue";
import { Image as ImageIcon, RotateCw, X } from "lucide-react";
import { Image } from "../Image";

const RADIUS = 11;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS; // ≈ 50.3

interface ImageUploadPreviewProps {
  item: Pick<ImageUploadItem, "file" | "previewUrl" | "status" | "progress">;
  order: number;
  onRemove: () => void;
  onRetry: () => void;
  className?: string;
}

export function ImageUploadPreview({ item, order, onRemove, onRetry, className }: ImageUploadPreviewProps) {
  const isLoading = item.status === "pending" || item.status === "uploading";
  const isError = item.status === "error";

  return (
    <div className={cn("overflow-hidden relative w-full rounded-8", className)}>
      <Image
        src={item.previewUrl}
        isHeic={isHeicFile(item.file)}
        alt=""
        aspectRatio="1/1"
        className="block object-cover w-full aspect-square"
        draggable={false}
        reportCacheError={false}
      />

      {/* 로딩 오버레이 */}
      {isLoading && (
        <div className="flex absolute inset-0 flex-col gap-1 justify-center items-center bg-black/40">
          <span className="inline-flex relative justify-center items-center w-8 h-8">
            {/* 배경 트랙 */}
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 24 24">
              <title>Loading progress</title>
              <circle
                cx="12"
                cy="12"
                r={RADIUS}
                fill="black"
                fillOpacity={0.5}
                stroke="white"
                strokeOpacity={0.3}
                strokeWidth="0"
              />
              {/* 진행 arc */}
              <circle
                cx="12"
                cy="12"
                r={RADIUS}
                fill="none"
                stroke="white"
                strokeWidth="1"
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={CIRCUMFERENCE * (1 - (item.progress ?? 0) / 100)}
                style={{ transition: "stroke-dashoffset 0.3s ease" }}
              />
            </svg>
            <ImageIcon className="w-4 h-4 text-white relative z-10" />
          </span>
          <span className="text-white text-caption1">{item.progress}%</span>
        </div>
      )}

      {/* 에러 오버레이 */}
      {isError && (
        <div className="flex absolute inset-0 flex-col gap-1 justify-center items-center bg-black/40">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
            className="inline-flex justify-center items-center w-8 h-8 text-white rounded-full bg-black/50"
          >
            <RotateCw width={20} height={20} />
          </button>
          <span className="text-white text-caption1">Failed to upload</span>
        </div>
      )}

      <span className="absolute w-6 h-6 left-[5px] top-[5px] inline-flex items-center justify-center rounded-full bg-white">
        <span className="text-gray-1000 text-caption1">{order}</span>
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute top-1 right-1 text-gray-500"
        aria-label="Remove image"
      >
        <X />
      </button>
    </div>
  );
}
